'use client';

/**
 * OnboardingSection Component
 *
 * Collapsible section with a header (icon + title + progress count) that
 * contains a list of OnboardingMilestone items. Locked sections appear
 * dimmed with a lock icon overlay.
 *
 * @see docs/plans/2026-02-20-DESIGN-astor-ai-native-onboarding-help.md
 */

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { alpha, Box, Collapse, Typography, useTheme } from '@mui/material';
import type { OnboardingSection as OnboardingSectionType } from '@ops-matrix/shared-types';
import { motion } from 'framer-motion';
import React, { useCallback, useMemo, useState } from 'react';

import { useI18n } from '../../hooks/useI18n';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { iconSizes } from '../../theme/typography';
import { OnboardingMilestone } from './OnboardingMilestone';

// ============================================================================
// TYPES
// ============================================================================

export interface OnboardingSectionProps {
  /** The section data */
  section: OnboardingSectionType;
  /** Set of completed milestone IDs */
  completedMilestones: Set<string>;
  /** Whether this section is locked */
  isLocked: boolean;
  /** Callback when a milestone's "Let Astor help" button is clicked */
  onMilestoneAstorAction: (milestoneId: string) => void;
  /** Whether this section should be initially expanded */
  defaultExpanded?: boolean;
}

// ============================================================================
// ICON MAP (Maps string icon names to MUI icon components or emoji fallback)
// ============================================================================

function getSectionIcon(iconName: string): string {
  const iconMap: Record<string, string> = {
    settings: '\u2699\uFE0F',
    people: '\uD83D\uDC65',
    users: '\uD83D\uDC65',
    rocket: '\uD83D\uDE80',
    chart: '\uD83D\uDCCA',
    shield: '\uD83D\uDEE1\uFE0F',
    document: '\uD83D\uDCC4',
    star: '\u2B50',
    lightbulb: '\uD83D\uDCA1',
    briefcase: '\uD83D\uDCBC',
    'book-open': '\uD83D\uDCD6',
    upload: '\uD83D\uDCE4',
    send: '\uD83D\uDCE8',
    handshake: '\uD83E\uDD1D',
  };
  return iconMap[iconName] || '\uD83D\uDCCB';
}

// ============================================================================
// COMPONENT
// ============================================================================

export function OnboardingSection({
  section,
  completedMilestones,
  isLocked,
  onMilestoneAstorAction,
  defaultExpanded = false,
}: OnboardingSectionProps): React.JSX.Element {
  const theme = useTheme();
  const { t } = useI18n();
  const prefersReducedMotion = useReducedMotion();

  const [expanded, setExpanded] = useState(defaultExpanded);

  const completedCount = useMemo(
    () => section.milestones.filter((m) => completedMilestones.has(m.id) || m.status === 'completed').length,
    [section.milestones, completedMilestones],
  );

  const totalCount = section.milestones.length;

  const handleToggle = useCallback(() => {
    if (!isLocked) {
      setExpanded((prev) => !prev);
    }
  }, [isLocked]);

  const sectionIcon = getSectionIcon(section.icon);

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: isLocked ? alpha(theme.palette.divider, 0.3) : 'divider',
        opacity: isLocked ? 0.6 : 1,
        transition: 'opacity 0.2s ease, border-color 0.2s ease',
      }}
      data-testid={`onboarding-section-${section.id}`}
    >
      {/* Section Header */}
      <Box
        component="button"
        onClick={handleToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          px: 2,
          py: 1.5,
          border: 'none',
          bgcolor: alpha(theme.palette.background.paper, 0.5),
          cursor: isLocked ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit',
          textAlign: 'left',
          transition: 'background-color 0.2s ease',
          '&:hover': !isLocked
            ? {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              }
            : undefined,
        }}
        disabled={isLocked}
        data-testid={`onboarding-section-header-${section.id}`}
      >
        {/* Section Icon */}
        <Box
          sx={{
            fontSize: '1.25rem',
            lineHeight: 1,
            flexShrink: 0,
          }}
          role="img"
          aria-hidden="true"
        >
          {isLocked ? <LockOutlinedIcon sx={{ fontSize: iconSizes.base, color: 'text.disabled' }} /> : sectionIcon}
        </Box>

        {/* Title + Progress */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: isLocked ? 'text.disabled' : 'text.primary',
            }}
          >
            {section.title}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isLocked
              ? t('astor.onboarding.sectionLocked')
              : t('astor.onboarding.completionLabel', { completed: completedCount, total: totalCount })}
          </Typography>
        </Box>

        {/* Expand/Collapse Icon */}
        {!isLocked && (
          <Box
            component={prefersReducedMotion ? 'span' : motion.span}
            {...(!prefersReducedMotion && {
              animate: { rotate: expanded ? 180 : 0 },
              transition: { duration: 0.2 },
            })}
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              ...(prefersReducedMotion && {
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }),
            }}
          >
            <ExpandMoreIcon sx={{ fontSize: iconSizes.base }} />
          </Box>
        )}
      </Box>

      {/* Milestones List — only rendered when not locked */}
      {!isLocked && (
        <Collapse in={expanded} unmountOnExit>
          <Box
            sx={{
              py: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            {section.milestones.map((milestone) => (
              <OnboardingMilestone
                key={milestone.id}
                milestone={milestone}
                isCompleted={completedMilestones.has(milestone.id) || milestone.status === 'completed'}
                onAstorAction={onMilestoneAstorAction}
                sectionLocked={isLocked}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

OnboardingSection.displayName = 'OnboardingSection';

export default OnboardingSection;
